import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layout,
  Card,
  Typography,
  Button,
  Space,
  Row,
  Col,
  Segmented,
  DatePicker,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Empty,
  Spin,
  message,
  Popconfirm,
  Badge,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  EnvironmentOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import API from '../config/api';
import DashboardHeader from './DashboardHeader';
import DashboardSidebar from './DashboardSidebar';
import { useClinicContext } from './ClinicContext';
import { useDoctorContext } from './DoctorContext';
import { useLanguage } from './LanguageContext';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

type CalendarView = 'day' | 'week' | 'month' | 'year';

interface DoctorAppointment {
  _id: string;
  doctor_id: string;
  clinic_id?: string;
  title: string;
  description?: string;
  location?: string;
  start_at: string;
  end_at: string;
  all_day: boolean;
  color: string;
  reminder_minutes: number;
  status: 'active' | 'cancelled';
}

interface AppointmentFormValues {
  title: string;
  description?: string;
  location?: string;
  range: [Dayjs, Dayjs];
  all_day?: boolean;
  reminder_minutes: number;
  color: string;
}

const COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#13c2c2'];
const AR_WEEKDAYS = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

function softColor(color: string) {
  const palette: Record<string, { bg: string; border: string; text: string }> = {
    '#1677ff': { bg: '#eef7ff', border: '#b9dcff', text: '#0b4f8a' },
    '#52c41a': { bg: '#effbea', border: '#bde8aa', text: '#2e6f16' },
    '#fa8c16': { bg: '#fff7e8', border: '#ffd591', text: '#9a4f00' },
    '#722ed1': { bg: '#f5f0ff', border: '#d3adf7', text: '#4b1b8f' },
    '#eb2f96': { bg: '#fff0f6', border: '#ffadd2', text: '#9e1068' },
    '#13c2c2': { bg: '#e6fffb', border: '#87e8de', text: '#006d75' },
  };
  return palette[color] || palette['#1677ff'];
}

function rangeForView(cursor: Dayjs, view: CalendarView) {
  if (view === 'day') return { from: cursor.startOf('day'), to: cursor.add(1, 'day').startOf('day') };
  if (view === 'week') return { from: cursor.startOf('week'), to: cursor.startOf('week').add(7, 'day') };
  if (view === 'year') return { from: cursor.startOf('year'), to: cursor.startOf('year').add(1, 'year') };
  return { from: cursor.startOf('month'), to: cursor.startOf('month').add(1, 'month') };
}

function viewTitle(cursor: Dayjs, view: CalendarView) {
  if (view === 'day') return cursor.format('dddd, D MMMM YYYY');
  if (view === 'week') {
    const start = cursor.startOf('week');
    return `${start.format('D MMM')} - ${start.add(6, 'day').format('D MMM YYYY')}`;
  }
  if (view === 'year') return cursor.format('YYYY');
  return cursor.format('MMMM YYYY');
}

function appointmentInDay(item: DoctorAppointment, day: Dayjs) {
  const start = dayjs(item.start_at);
  const end = dayjs(item.end_at);
  return start.isBefore(day.endOf('day')) && end.isAfter(day.startOf('day'));
}

const DoctorAppointments: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [view, setView] = useState<CalendarView>('month');
  const [cursor, setCursor] = useState<Dayjs>(dayjs());
  const [appointments, setAppointments] = useState<DoctorAppointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DoctorAppointment | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<AppointmentFormValues>();

  const navigate = useNavigate();
  const { doctorId, doctorName } = useDoctorContext();
  const { selectedClinicId, selectedClinic } = useClinicContext();
  const { isRTL } = useLanguage();

  const effectiveDoctorId = doctorId || localStorage.getItem('doctorId');
  const effectiveClinicId = selectedClinicId && selectedClinicId !== '__all_clinics__'
    ? selectedClinicId
    : localStorage.getItem('clinicId') || '';

  const visibleRange = useMemo(() => rangeForView(cursor, view), [cursor, view]);

  const fetchAppointments = useCallback(async () => {
    if (!effectiveDoctorId) return;
    try {
      setLoading(true);
      const params: Record<string, string> = {
        from: visibleRange.from.toISOString(),
        to: visibleRange.to.toISOString(),
      };
      if (effectiveClinicId) params.clinic_id = effectiveClinicId;
      const res = await axios.get(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_APPOINTMENTS(effectiveDoctorId)}`,
        { params }
      );
      if (res.data?.success) {
        setAppointments(res.data.appointments || []);
      } else {
        throw new Error(res.data?.message || 'Failed to load appointments');
      }
    } catch (error: any) {
      message.error(error?.message || 'تعذر تحميل المواعيد');
    } finally {
      setLoading(false);
    }
  }, [effectiveDoctorId, effectiveClinicId, visibleRange.from, visibleRange.to]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const appointmentsByDay = useMemo(() => {
    const map = new Map<string, DoctorAppointment[]>();
    appointments.forEach((item) => {
      const key = dayjs(item.start_at).format('YYYY-MM-DD');
      map.set(key, [...(map.get(key) || []), item]);
    });
    return map;
  }, [appointments]);

  const openCreate = (day: Dayjs = cursor, hour = 9) => {
    const start = day.hour(hour).minute(0).second(0);
    setEditing(null);
    form.setFieldsValue({
      title: '',
      description: '',
      location: selectedClinic?.name || '',
      range: [start, start.add(1, 'hour')],
      all_day: false,
      reminder_minutes: 15,
      color: COLORS[0],
    });
    setModalOpen(true);
  };

  const openEdit = (item: DoctorAppointment) => {
    setEditing(item);
    form.setFieldsValue({
      title: item.title,
      description: item.description || '',
      location: item.location || '',
      range: [dayjs(item.start_at), dayjs(item.end_at)],
      all_day: item.all_day,
      reminder_minutes: item.reminder_minutes,
      color: item.color || COLORS[0],
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const saveAppointment = async (values: AppointmentFormValues) => {
    if (!effectiveDoctorId) {
      message.error('Doctor ID not found');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title: values.title,
        description: values.description || '',
        location: values.location || '',
        clinic_id: effectiveClinicId,
        start_at: values.range[0].toISOString(),
        end_at: values.range[1].toISOString(),
        all_day: !!values.all_day,
        reminder_minutes: Number(values.reminder_minutes),
        color: values.color || COLORS[0],
        updated_by: effectiveDoctorId,
        created_by: effectiveDoctorId,
      };
      if (editing) {
        await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_APPOINTMENT(effectiveDoctorId, editing._id)}`,
          payload
        );
        message.success('Appointment updated');
      } else {
        await axios.post(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_APPOINTMENTS(effectiveDoctorId)}`, payload);
        message.success('Appointment created');
      }
      closeModal();
      fetchAppointments();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Could not save appointment');
    } finally {
      setSaving(false);
    }
  };

  const deleteAppointment = async () => {
    if (!effectiveDoctorId || !editing) return;
    try {
      setSaving(true);
      await axios.delete(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_APPOINTMENT(effectiveDoctorId, editing._id)}`);
      message.success('Appointment deleted');
      closeModal();
      fetchAppointments();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Could not delete appointment');
    } finally {
      setSaving(false);
    }
  };

  const moveCursor = (direction: 1 | -1) => {
    if (view === 'day') setCursor(cursor.add(direction, 'day'));
    else if (view === 'week') setCursor(cursor.add(direction, 'week'));
    else if (view === 'year') setCursor(cursor.add(direction, 'year'));
    else setCursor(cursor.add(direction, 'month'));
  };

  const appointmentCard = (item: DoctorAppointment, compact = false) => {
    const theme = softColor(item.color || COLORS[0]);
    return (
      <button
        type="button"
        key={item._id}
        className={`doctor-appointment-card ${compact ? 'doctor-appointment-card--compact' : ''}`}
        style={{
          borderColor: theme.border,
          borderRightColor: item.color || COLORS[0],
          background: theme.bg,
          color: theme.text,
        }}
        onClick={() => openEdit(item)}
      >
        <span className="doctor-appointment-time">
          {item.all_day ? 'طوال اليوم' : dayjs(item.start_at).format('h:mm A')}
        </span>
        <strong>{item.title}</strong>
        {(item.description || (!compact && item.location)) && (
          <small>
            {!compact && item.location ? <><EnvironmentOutlined /> {item.location}</> : item.description}
          </small>
        )}
      </button>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 17 }, (_, index) => index + 7);
    return (
      <div className="doctor-calendar-day">
        {hours.map((hour) => {
          const hourItems = appointments.filter((item) =>
            appointmentInDay(item, cursor) && dayjs(item.start_at).hour() === hour
          );
          return (
            <div className="doctor-calendar-hour" key={hour}>
              <div className="doctor-calendar-hour-label">{String(hour).padStart(2, '0')}:00</div>
              <div className="doctor-calendar-hour-body">
                {hourItems.length > 0 ? hourItems.map((item) => appointmentCard(item)) : (
                  <Button type="dashed" onClick={() => openCreate(cursor, hour)}>+ Add appointment</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = cursor.startOf('week');
    const days = Array.from({ length: 7 }, (_, index) => weekStart.add(index, 'day'));
    return (
      <div className="doctor-calendar-week">
        {days.map((day) => {
          const dayItems = appointments.filter((item) => appointmentInDay(item, day));
          return (
            <div key={day.format('YYYY-MM-DD')}>
              <Card
                size="small"
                className="doctor-calendar-week-card"
                title={
                  <button className="doctor-calendar-day-title" onClick={() => { setView('day'); setCursor(day); }}>
                    <span>{day.format('ddd')}</span>
                    <strong>{day.format('D')}</strong>
                  </button>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {dayItems.map((item) => appointmentCard(item, true))}
                  <Button size="small" block type="dashed" onClick={() => openCreate(day, 9)}>
                    <PlusOutlined /> Add
                  </Button>
                </Space>
              </Card>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMonthView = () => {
    const start = cursor.startOf('month').startOf('week');
    const days = Array.from({ length: 42 }, (_, index) => start.add(index, 'day'));
    return (
      <div
        className="doctor-calendar-month"
        style={{
          direction: 'rtl',
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(160px, 1fr))',
          width: '100%',
          minWidth: 1120,
          background: '#fff',
          border: '1px solid #dfe7ee',
          borderBottom: 0,
          borderLeft: 0,
        }}
      >
        {AR_WEEKDAYS.map((dayName) => (
          <div
            className="doctor-calendar-month-weekday"
            key={dayName}
            style={{
              padding: '14px 12px',
              minHeight: 48,
              background: '#fbfdff',
              borderBottom: '1px solid #dfe7ee',
              borderLeft: '1px solid #dfe7ee',
              color: '#18324a',
              fontSize: 15,
              fontWeight: 900,
              textAlign: 'center',
            }}
          >
            {dayName}
          </div>
        ))}
        {days.map((day) => {
          const key = day.format('YYYY-MM-DD');
          const dayItems = appointmentsByDay.get(key) || [];
          const muted = day.month() !== cursor.month();
          const isToday = day.isSame(dayjs(), 'day');
          return (
            <div
              className={`doctor-calendar-month-cell ${muted ? 'doctor-calendar-month-cell--muted' : ''} ${isToday ? 'doctor-calendar-month-cell--today' : ''}`}
              key={key}
              style={{
                minHeight: 165,
                padding: '10px 10px 12px',
                background: isToday ? '#eef7ff' : muted ? '#fbfbfc' : '#fff',
                borderBottom: '1px solid #e6edf2',
                borderLeft: '1px solid #e6edf2',
                boxShadow: isToday ? 'inset 0 0 0 1px #b9dcff' : undefined,
                overflow: 'hidden',
              }}
            >
              <button
                className="doctor-calendar-month-date"
                style={{
                  border: 0,
                  background: 'transparent',
                  color: muted ? '#b7c3cc' : '#167078',
                  cursor: 'pointer',
                  fontSize: 18,
                  fontWeight: 800,
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  padding: 0,
                }}
                onClick={() => { setView('day'); setCursor(day); }}
              >
                {day.date()}
              </button>
              <div
                className="doctor-calendar-month-items"
                style={{ display: 'grid', gap: 8, marginTop: 12 }}
              >
                {dayItems.slice(0, 3).map((item) => appointmentCard(item, true))}
                {dayItems.length > 3 && <Tag>+{dayItems.length - 3}</Tag>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderYearView = () => {
    const months = Array.from({ length: 12 }, (_, index) => cursor.startOf('year').add(index, 'month'));
    return (
      <Row gutter={[16, 16]}>
        {months.map((month) => {
          const monthCount = appointments.filter((item) => dayjs(item.start_at).month() === month.month()).length;
          return (
            <Col xs={24} sm={12} md={8} xl={6} key={month.month()}>
              <Card hoverable onClick={() => { setView('month'); setCursor(month); }}>
                <Space direction="vertical">
                  <Text strong>{month.format('MMMM')}</Text>
                  <Badge count={monthCount} showZero color="#1677ff" />
                  <Text type="secondary">appointments</Text>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout>
        <DashboardHeader onSettingsClick={() => navigate('/settings')} onMenuClick={() => {}} isMobile={false} />
        <Content
          className="doctor-appointments-page"
          dir="rtl"
          style={{
            margin: 0,
            padding: 24,
            background: 'linear-gradient(180deg, #eef4f8 0%, #f7f9fc 100%)',
            minHeight: 'calc(100vh - 64px)',
            overflowX: 'auto',
          }}
        >
          <div
            className="doctor-appointments-topbar"
            style={{
              minHeight: 96,
              marginBottom: 16,
              padding: '18px 22px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #0d2743 0%, #113d64 55%, #0c223a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              boxShadow: '0 10px 28px rgba(8, 33, 58, 0.18)',
            }}
          >
            <div className="doctor-appointments-title-block" style={{ display: 'grid', gap: 4 }}>
              <Text className="doctor-appointments-kicker">نظام إدارة العيادة</Text>
              <Space wrap>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>
                  لوحة التحكم
                </Button>
                <Title level={3} style={{ margin: 0, color: '#fff' }}>
                  <CalendarOutlined /> مواعيدي
                </Title>
              </Space>
              <Text style={{ color: 'rgba(255,255,255,0.78)' }}>
                {doctorName ? `د. ${doctorName}` : 'تقويم الطبيب'}
                {selectedClinic?.name ? ` - ${selectedClinic.name}` : ''}
              </Text>
            </div>
            <div className="doctor-appointments-top-actions">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(cursor, 9)}>
                موعد جديد
              </Button>
            </div>
          </div>

          <Card
            className="doctor-calendar-controls"
            bordered={false}
            style={{ borderRadius: '8px 8px 0 0', marginBottom: 0 }}
            bodyStyle={{ padding: '14px 18px' }}
          >
            <Row align="middle" gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Space>
                  <Button icon={<RightOutlined />} onClick={() => moveCursor(1)} />
                  <Button onClick={() => setCursor(dayjs())}>اليوم</Button>
                  <Button icon={<LeftOutlined />} onClick={() => moveCursor(-1)} />
                </Space>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>{viewTitle(cursor, view)}</Title>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: isRTL ? 'left' : 'right' }}>
                <Space wrap>
                  <DatePicker value={cursor} onChange={(value) => value && setCursor(value)} picker={view === 'year' ? 'year' : 'date'} />
                  <Segmented
                    value={view}
                    onChange={(value) => setView(value as CalendarView)}
                    options={[
                      { label: 'يوم', value: 'day' },
                      { label: 'أسبوع', value: 'week' },
                      { label: 'شهر', value: 'month' },
                      { label: 'سنة', value: 'year' },
                    ]}
                  />
                </Space>
              </Col>
            </Row>
          </Card>

          <Card
            className="doctor-calendar-surface"
            bordered={false}
            style={{ borderRadius: '0 0 8px 8px', overflow: 'hidden' }}
            bodyStyle={{ padding: 0, overflowX: 'auto' }}
          >
            <Spin spinning={loading}>
              {!effectiveDoctorId ? (
                <Empty description="Doctor ID not found. Please log in again." />
              ) : (
                <>
                  {view === 'day' && renderDayView()}
                  {view === 'week' && renderWeekView()}
                  {view === 'month' && renderMonthView()}
                  {view === 'year' && renderYearView()}
                </>
              )}
            </Spin>
          </Card>
        </Content>
      </Layout>

      <Modal
        title={editing ? 'Edit Appointment' : 'New Appointment'}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Form<AppointmentFormValues> form={form} layout="vertical" onFinish={saveAppointment}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input maxLength={160} placeholder="e.g. Surgery follow-up" />
          </Form.Item>
          <Form.Item name="description" label="Details">
            <TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="range"
                label="Time"
                rules={[{ required: true, message: 'Start and end time are required' }]}
              >
                <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="location" label="Location">
                <Input prefix={<EnvironmentOutlined />} maxLength={200} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="reminder_minutes" label="Reminder">
                <Select>
                  <Select.Option value={0}>No reminder</Select.Option>
                  <Select.Option value={5}>5 minutes before</Select.Option>
                  <Select.Option value={15}>15 minutes before</Select.Option>
                  <Select.Option value={30}>30 minutes before</Select.Option>
                  <Select.Option value={60}>1 hour before</Select.Option>
                  <Select.Option value={1440}>1 day before</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="all_day" label="All day" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="color" label="Color">
            <Select>
              {COLORS.map((color) => (
                <Select.Option key={color} value={color}>
                  <span className="doctor-appointment-color-option" style={{ background: color }} />
                  {color}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div className="doctor-appointment-modal-actions">
            {editing && (
              <Popconfirm title="Delete this appointment?" onConfirm={deleteAppointment} okButtonProps={{ danger: true }}>
                <Button danger icon={<DeleteOutlined />} loading={saving}>Delete</Button>
              </Popconfirm>
            )}
            <Space>
              <Button onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                Save
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
};

export default DoctorAppointments;
